import moment from 'moment-timezone';
import React, { useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import AsOfDate from '../../../core/components/AsOfDate';
import DataTable from '../../../core/components/DataTable';
import * as utils from '../../../core/utils';
import { selectSiteName } from '../../settings/reducer';
import { setFromDate, setUntilDate } from '../../tasks/actions';
import { Interval } from '../../tasks/models';
import { selectFromDate, selectUntilDate } from '../../tasks/reducer';
import { TaskCompletionStatus } from '../../tasks/types';
import { clearTaskCompletionsForTask, getTaskCompletionsForTask } from '../actions';
import {
  selectTaskCompletionsForTask,
  selectTaskCompletionsForTaskPage,
  selectTaskCompletionsForTaskPageSize,
  selectTaskCompletionsForTaskTotal,
} from '../reducer';
import { useAppContext } from '../../../providers/AppProvider';

const History = (props) => {
  const { task } = props;

  // subscribes
  const { timeZone } = useAppContext();
  const taskCompletions = useSelector(selectTaskCompletionsForTask);
  const page = useSelector(selectTaskCompletionsForTaskPage);
  const pageSize = useSelector(selectTaskCompletionsForTaskPageSize);
  const total = useSelector(selectTaskCompletionsForTaskTotal);
  const fromDate = useSelector(selectFromDate);
  const untilDate = useSelector(selectUntilDate);
  const siteName = useSelector(selectSiteName);
  const intl = useIntl();
  // actions
  const dispatch = useDispatch();

  const getTaskCompletionPage = useCallback(
    (newPage) => {
      dispatch(
        getTaskCompletionsForTask({
          id: task.id,
          page: newPage,
          pageSize: 10,
          fromDate: fromDate.format('YYYY-MM-DD'),
          untilDate: untilDate.format('YYYY-MM-DD'),
        })
      );
    },
    [task.id, fromDate, untilDate]
  );

  useEffect(() => {
    getTaskCompletionPage(0);
  }, [getTaskCompletionPage]);

  useEffect(() => {
    return () => {
      dispatch(clearTaskCompletionsForTask());
    };
  }, [clearTaskCompletionsForTask]);

  // Handle updates when fromDate, task, or untilDate change
  useEffect(() => {
    getTaskCompletionPage(0);
  }, [fromDate, task, untilDate, getTaskCompletionPage]);

  const getPaginationInfo = useCallback(() => {
    return {
      total,
      page,
      pageSize,
      goToPage: (newPage) => getTaskCompletionPage(newPage),
    };
  }, [total, page, pageSize]);

  const getDue = useCallback(
    (taskCompletion) => {
      if (task.interval === Interval.CarCount) {
        return taskCompletion.scheduleCarCount;
      }
      if (taskCompletion.scheduleEnd) {
        if (task.interval === Interval.Hourly) {
          return moment.tz(taskCompletion.scheduleEnd, timeZone).format('MM/DD/YYYY hh:mm a');
        }
        return moment.tz(taskCompletion.scheduleEnd, timeZone).format('MM/DD/YYYY');
      }
      return intl.formatMessage({ id: 'notAvailable' });
    },
    [task.interval, intl, timeZone]
  );

  const initializeDatatable = useCallback(
    (taskCompletionsIntl) => {
      const data = taskCompletionsIntl.map((taskCompletion) => {
        const employee = taskCompletion.employee;
        const due = getDue(taskCompletion);
        const late =
          moment().utc().isAfter(moment(taskCompletion.scheduleEnd).utc()) &&
          utils.toEnum(TaskCompletionStatus, taskCompletion.status) ===
            TaskCompletionStatus.Pending;
        const rowData = {
          _id: taskCompletion.id,
          due,
          status: late ? intl.formatMessage({ id: 'expired' }) : taskCompletion.status,
          timestamp:
            taskCompletion.status !== 'Pending'
              ? moment.tz(taskCompletion.timestamp, timeZone).format('MM/DD/YYYY h:mm:ss a')
              : '',
          carCount: taskCompletion.carCount,
          employee: employee ? `${employee.firstName} ${employee.lastName}` : '',
        };
        if (task.interval !== Interval.CarCount) {
          delete rowData.carCount;
        }
        return rowData;
      });

      const headers = [
        { name: intl.formatMessage({ id: 'due' }) },
        { name: intl.formatMessage({ id: 'status' }) },
        { name: intl.formatMessage({ id: 'timestamp' }) },
        { name: intl.formatMessage({ id: 'carCount' }) },
        { name: intl.formatMessage({ id: 'teamMember' }) },
      ];
      if (task.interval !== Interval.CarCount) {
        headers.splice(3, 1);
      }

      return {
        data,
        headers,
      };
    },
    [getDue, intl, task.interval, timeZone]
  );

  const fetchAsOfDate = useCallback(() => {
    getTaskCompletionPage(0);
  }, []);

  return (
    <div>
      <div className="as-of-date-container">
        <AsOfDate
          fromDate={fromDate}
          untilDate={untilDate}
          setFromDate={(date) => dispatch(setFromDate(date))}
          setUntilDate={(date) => dispatch(setUntilDate(date))}
          fetchWithQuery={fetchAsOfDate}
          title={`${intl.formatMessage({ id: 'tasks' })}: `}
          dateTarget="history"
          intl={intl}
        />
      </div>
      {task.isEssentialTask && (
        <div className="mt-2">{`${intl.formatMessage({
          id: 'site',
        })}: ${siteName}`}</div>
      )}
      <DataTable paginationInfo={getPaginationInfo()} {...initializeDatatable(taskCompletions)} />
    </div>
  );
};

export default History;
